import { useState, useRef, useEffect } from "react";
import styles from "./SearchableSelect.module.css";

const SearchableSelect = ({
  value,
  onChange,
  onBlur,
  options = [],
  onSearch,
  placeholder = "Select...",
  searchPlaceholder = "Type to search...",
  className = "",
  width,
  error = false,
  disabled = false,
  // Label to display for a pre-set `value` that isn't in `options` yet and hasn't
  // been picked from the dropdown (e.g. a form pre-filled with the current value).
  initialLabel = "",
  // false = the same dropdown without the search box, for short option lists.
  searchable = true,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [cachedLabel, setCachedLabel] = useState("");
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Debounced server-side search.
  //
  // `onSearch` is deliberately NOT a dependency, and is called through a ref instead. Callers
  // almost always pass a function defined in their render body, so its identity changes on every
  // re-render — and since the search RESULT is what re-renders the caller (new options), keeping
  // it in the deps made this a self-sustaining loop: respond → re-render → new identity → re-arm
  // the timer → fire the SAME search again, ~3 requests/second for as long as the picker stayed
  // mounted. The geo city picker was doing exactly that to /api/geo/cities.
  //
  // The ref is updated on every render, so a fired search still uses the LATEST callback (and
  // therefore the caller's current props) — only the re-triggering is dropped.
  const onSearchRef = useRef(onSearch);
  useEffect(() => { onSearchRef.current = onSearch; });

  useEffect(() => {
    if (!onSearchRef.current) return;
    const timer = setTimeout(() => onSearchRef.current?.(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  // Clear cached label when value is cleared externally
  useEffect(() => {
    if (!value) setCachedLabel("");
  }, [value]);

  const filtered = onSearch || !searchable
    ? options
    : options.filter((o) =>
        String(o?.label ?? "").toLowerCase().includes((query || "").toLowerCase())
      );

  const selectedOption = options.find((o) => String(o.value) === String(value));
  // What the trigger shows: matched option label → picked label → pre-set label.
  const resolvedLabel = selectedOption ? selectedOption.label : (value ? (cachedLabel || initialLabel) : "");

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        if (onBlur) onBlur();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // `onSearch` was listed here but never used by this effect — it only churned the listener.
  }, [onBlur]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const handleSelect = (option) => {
    onChange(option.value);
    setCachedLabel(option.label);
    setOpen(false);
  };

  return (
    <div className={`${styles.wrapper} ${className}`} style={width ? { width } : undefined} ref={wrapperRef}>
      <div
        className={`${styles.trigger} ${error ? styles.triggerError : ""} ${open ? styles.triggerOpen : ""} ${disabled ? styles.triggerDisabled : ""}`}
        onClick={() => {
          if (disabled) return;
          if (!open) {
            // Clear search when reopening to allow new searches
            setQuery("");
            if (onSearch) onSearch("");
          }
          setOpen(!open);
        }}
      >
        <span className={resolvedLabel ? styles.selectedText : styles.placeholder}>
          {resolvedLabel || placeholder}
        </span>
        <span className={`${styles.arrow} ${open ? styles.arrowUp : ""}`}>&#9662;</span>
      </div>

      {open && (
        <div className={styles.dropdown}>
          {searchable && (
            <div className={styles.searchWrapper}>
              <input
                ref={inputRef}
                type="text"
                className={styles.searchInput}
                placeholder={searchPlaceholder}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          <div className={styles.optionsList}>
            {filtered.length === 0 ? (
              <div className={styles.noResults}>No results found</div>
            ) : (
              filtered.map((option) => (
                <div
                  key={option.value}
                  className={`${styles.option} ${String(option.value) === String(value) ? styles.optionSelected : ""}`}
                  onClick={() => handleSelect(option)}
                >
                  {option.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
